/**
 * User Edit Page
 * Page for editing an existing user
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { UserForm } from './components';
import { PageShell } from '../../components';

// Services and types
import { userService } from '../../services/userService';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import {
  User,
  UserFormData,
  UserEditFormData,
  UpdateUserRequest,
  removeWhatsAppMask,
  formatCPF,
  getRoleLabel,
} from '../../types/user';

const UserEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      if (!id) {
        setFalha({ mensagem: 'ID do usuário não fornecido' });
        setLoadingUser(false);
        return;
      }

      try {
        console.log('[UserEditPage] Loading user:', id);
        const userData = await userService.getUserById(parseInt(id));
        console.log('[UserEditPage] User loaded:', userData);
        setUser(userData);
      } catch (err: unknown) {
        console.error('[UserEditPage] Error loading user:', err);
        const falhaAoCarregar = falhaDeCarga(err, 'Não foi possível carregar o usuário. Tente de novo.');
        setFalha(falhaAoCarregar);
        if (falhaAoCarregar.status !== 403 && falhaAoCarregar.status !== 404) {
          enqueueSnackbar(falhaAoCarregar.mensagem, { variant: 'error' });
        }
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [id, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (data: UserFormData | UserEditFormData) => {
    const editData = data as UserEditFormData;
    if (!id || !user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('[UserEditPage] Updating user:', { id, data: editData });

      const request: UpdateUserRequest = {
        name: editData.name,
        whatsapp: editData.whatsapp ? removeWhatsAppMask(editData.whatsapp) : undefined,
        situacao: editData.situacao,
      };

      await userService.updateUser(parseInt(id), request);

      console.log('[UserEditPage] User updated successfully');
      enqueueSnackbar('Usuário atualizado com sucesso!', { variant: 'success' });

      // Navigate back to user details
      navigate(`/users/${id}`);
    } catch (err: unknown) {
      console.error('[UserEditPage] Error updating user:', err);
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao atualizar usuário';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/users/${id}`);
  };

  // Loading state
  if (loadingUser) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Usuários', path: '/users' },
          { label: 'Editar' },
        ]}
        title="Editar Usuário"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  // Load error state
  if (falha || !user) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Usuários', path: '/users' },
          { label: 'Editar' },
        ]}
        title="Editar Usuário"
      >
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Usuário não encontrado.')}
          rotuloDoBotao="Voltar para usuários"
          aoVoltar={() => navigate('/users')}
        />
      </PageShell>
    );
  }

  // Prepare initial data for form
  const initialData: Partial<UserEditFormData> = {
    name: user.name,
    whatsapp: user.whatsapp ?? '',
    situacao: user.situacao,
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Usuários', path: '/users' },
        { label: user.name, path: `/users/${id}` },
        { label: 'Editar' },
      ]}
      title="Editar Usuário"
      subtitle="Altere os dados do usuário abaixo"
    >
      {/* Read-only info */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Informações não editáveis
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                E-mail
              </Typography>
              <Typography variant="body1">{user.email}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                CPF
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {formatCPF(user.cpf)}
              </Typography>
            </Box>
            {user.roles && user.roles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Papéis
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.roles.map((role) => (
                    <Chip
                      key={role.id}
                      label={role.displayName || getRoleLabel(role.name)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Form */}
      <UserForm
        mode="edit"
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
        error={error}
      />
    </PageShell>
  );
};

export default UserEditPage;
